import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import { MaterialReactTable } from "material-react-table";
import * as XLSX from "xlsx";
import { useOutletContext } from "react-router-dom";
import toastr from "../../utils/toast";
import { openInAppTab } from "../../utils/tabNavigation";

// Major function that the legacy page never offered in this search.
const EXCLUDED_FUNCTION_IDS = [23];

const EMPTY_FORM = {
  searchType: "by_candidate",
  compensation: false,
  roles: [],
  keyword: [],
  name: "",
  designation: "",
  linkedin_url: "",
  company: [],
  prev_company: [],
  dutymaster: null,
  region_only: false,
  countries: [],
  regions: [],
  industrymaster: null,
  industries: [],
};

function AdvSearchCandidates() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { setGlobalLoading, token } = useOutletContext();
  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token],
  );

  const [form, setForm] = useState(EMPTY_FORM);
  const [functions, setFunctions] = useState([]);
  const [masterIndustries, setMasterIndustries] = useState([]);
  const [minorIndustries, setMinorIndustries] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [myRoles, setMyRoles] = useState([]);

  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [searched, setSearched] = useState(false);
  const [lastQuery, setLastQuery] = useState(null);
  const [rowSelection, setRowSelection] = useState({});
  const [targetRole, setTargetRole] = useState(null);

  const debounceTimers = useRef({});

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const isCandidateSearch = form.searchType === "by_candidate";

  // ---------------------------------------------------------------- lookups
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setGlobalLoading(true);
        const [fn, ind, map, roles] = await Promise.all([
          axios.get(`${API_BASE_URL}/common_controller/get_major_functions`, authHeaders),
          axios.get(`${API_BASE_URL}/common_controller/get_master_industry_list`, authHeaders),
          axios.get(`${API_BASE_URL}/common_controller/country_region_map`, authHeaders),
          axios.get(`${API_BASE_URL}/common_controller/get_my_roles`, authHeaders),
        ]);
        if (cancelled) return;
        setFunctions(
          (fn.data.options || []).filter(
            (o) => !EXCLUDED_FUNCTION_IDS.includes(Number(o.value)),
          ),
        );
        setMasterIndustries(ind.data.options || []);
        setMyRoles(roles.data.options || []);

        const regions = new Map();
        const countries = [];
        (map.data || []).forEach((r) => {
          regions.set(r.region_id, { value: r.region_id, label: r.region_name });
          // Region 9 was left out of the country list on the legacy page.
          if (r.country_id && Number(r.region_id) !== 9) {
            countries.push({
              value: r.country_id,
              label: `${r.country} (${r.region_name})`,
            });
          }
        });
        setRegionOptions([...regions.values()]);
        setCountryOptions(countries);
      } catch (error) {
        console.error(error);
        toastr.error("Could not load the search options.");
      } finally {
        if (!cancelled) setGlobalLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL, token]);

  // Minor industries follow the chosen major industry.
  useEffect(() => {
    setField("industries", []);
    if (!form.industrymaster) {
      setMinorIndustries([]);
      return;
    }
    let cancelled = false;
    axios
      .get(
        `${API_BASE_URL}/common_controller/get_minor_industries_by_maji_id/${form.industrymaster.value}`,
        authHeaders,
      )
      .then((res) => {
        if (cancelled) return;
        const names = [
          ...new Set(
            (res.data.minor_industries || [])
              .map((i) => i.new_category_name || i.name)
              .filter(Boolean),
          ),
        ];
        setMinorIndustries(names.map((n) => ({ value: n, label: n })));
      })
      .catch((error) => console.error(error));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.industrymaster]);

  // ---------------------------------------------------------- autocomplete
  // Debounced so a fast typist sends one request, not one per key.
  const debouncedLoad = (key, fetcher) => (input) =>
    new Promise((resolve) => {
      clearTimeout(debounceTimers.current[key]);
      if (!input || input.trim().length < 2) {
        resolve([]);
        return;
      }
      debounceTimers.current[key] = setTimeout(async () => {
        try {
          resolve(await fetcher(input.trim()));
        } catch (error) {
          console.error(error);
          resolve([]);
        }
      }, 300);
    });

  const loadSkills = debouncedLoad("skills", async (input) => {
    const res = await axios.post(
      `${API_BASE_URL}/ai_candidates/get_skills_options`,
      { keywords: input },
      authHeaders,
    );
    return res.data.keywords || [];
  });

  const loadCompanies = debouncedLoad("companies", async (input) => {
    const res = await axios.get(
      `${API_BASE_URL}/common_controller/get_companyinfoslist`,
      { ...authHeaders, params: { search: input } },
    );
    return (res.data.options || res.data || [])
      .filter((c) => c.tbl !== "custom")
      .map((c) => ({ value: c.name, label: c.name }));
  });

  // ----------------------------------------------------------------- search
  const buildQuery = () => ({
    searchType: form.searchType,
    compensation: form.compensation,
    roleArray_id: form.roles.map((r) => r.value),
    keyword: form.keyword.map((k) => k.value),
    name: form.name,
    designation: form.designation,
    linkedin_url: form.linkedin_url,
    company: form.company.map((c) => c.value),
    prev_company: form.prev_company.map((c) => c.value),
    dutymaster_id: form.dutymaster ? form.dutymaster.value : 0,
    country_id: form.region_only ? [] : form.countries.map((c) => c.value),
    region_id: form.region_only ? form.regions.map((r) => r.value) : [],
    industrymaster_id: form.industrymaster ? form.industrymaster.value : 0,
    industry_id: form.industries.map((i) => i.value),
  });

  const runSearch = async (query, offset) => {
    try {
      setGlobalLoading(true);
      const res = await axios.post(
        `${API_BASE_URL}/candidates/adv_search`,
        { ...query, offset },
        authHeaders,
      );
      const incoming = res.data.rows || [];
      setRows((prev) => (offset === 0 ? incoming : [...prev, ...incoming]));
      setHasMore(Boolean(res.data.hasMore));
      setSearched(true);
      if (offset === 0) setRowSelection({});
    } catch (error) {
      console.error(error);
      toastr.error(error.response?.data?.error || "Search failed.");
    } finally {
      setGlobalLoading(false);
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    const query = buildQuery();
    setLastQuery(query);
    runSearch(query, 0);
  };

  const onMore = () => {
    if (lastQuery) runSearch(lastQuery, rows.length);
  };

  const onReset = () => {
    setForm(EMPTY_FORM);
    setRows([]);
    setHasMore(false);
    setSearched(false);
    setLastQuery(null);
    setRowSelection({});
    setTargetRole(null);
  };

  // ------------------------------------------------------------ bulk actions
  const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);

  const addToRole = async () => {
    if (!targetRole) {
      toastr.error("Please select a role.");
      return;
    }
    try {
      setGlobalLoading(true);
      const res = await axios.post(
        `${API_BASE_URL}/ai_candidates/map_to_role`,
        { data: { selectedId: selectedIds, selectedRole: targetRole.value } },
        authHeaders,
      );
      if (res.data.success === true) {
        toastr.success(res.data.message);
        setRowSelection({});
        setTargetRole(null);
      } else {
        toastr.error(res.data.message);
      }
    } catch (error) {
      console.error(error);
      toastr.error("Could not add the candidates to the role.");
    } finally {
      setGlobalLoading(false);
    }
  };

  const exportExcel = () => {
    const showComp = Boolean(lastQuery?.compensation);
    const data = rows.map((r) => ({
      Name: r.name || "",
      Email: r.encrypted ? "Encrypted" : r.email || "",
      Phone: r.encrypted ? "Encrypted" : r.phone || "",
      Designation: r.designation || "",
      "Current Company": r.company || "",
      ...(showComp
        ? { Currency: r.currency || "", Compensation: r.compensation ?? "" }
        : {}),
      Country: r.country || "",
      City: r.city || "",
      LinkedIn: r.linkedin_url || "",
    }));
    const sheet = XLSX.utils.json_to_sheet(data);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "Candidates");
    XLSX.writeFile(book, "Export Candidate.xlsx");
  };

  // ----------------------------------------------------------------- table
  const openCandidate = (id) => openInAppTab(`/candidates/view/${id}`);

  const columns = useMemo(() => {
    const cols = [
      {
        header: "Full Name",
        accessorKey: "name",
        size: 260,
        Cell: ({ row }) => {
          const r = row.original;
          return (
            <span>
              <a
                href={`/candidates/view/${r.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  openCandidate(r.id);
                }}
              >
                {r.name || "-"}
              </a>
              {/* BADGES (gold / silver): render r.badge here once the backend returns it. */}
              {r.has_resume && (
                <span className="badge bg-secondary ms-1" title="Resume available">
                  R
                </span>
              )}
              {r.gdpr && (
                <span className="badge bg-dark ms-1" title="GDPR - details hidden">
                  GDPR
                </span>
              )}
              {r.offer_company !== null && (
                <span
                  className="badge bg-danger ms-1"
                  title={
                    r.offer_company
                      ? `Offer / joined: ${r.offer_company}`
                      : "Offer / joined"
                  }
                >
                  Offer
                </span>
              )}
              {r.referred && (
                <span className="badge bg-warning text-dark ms-1" title="Referred">
                  Ref
                </span>
              )}
              {r.linkedin_url && (
                <a
                  className="btn btn-sm btn-outline-primary ms-1 py-0 px-1"
                  href={
                    /^https?:\/\//i.test(r.linkedin_url)
                      ? r.linkedin_url
                      : `https://${r.linkedin_url}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  title="LinkedIn"
                >
                  Li
                </a>
              )}
            </span>
          );
        },
      },
      {
        header: "Email",
        accessorKey: "email",
        Cell: ({ row }) =>
          row.original.encrypted ? <b>Encrypted</b> : row.original.email || "-",
      },
      {
        header: "Phone",
        accessorKey: "phone",
        Cell: ({ row }) =>
          row.original.encrypted ? "Encrypted" : row.original.phone || "-",
      },
      {
        header: "Designation",
        accessorKey: "designation",
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        header: "Current Company",
        accessorKey: "company",
        Cell: ({ cell }) => cell.getValue() || "-",
      },
    ];
    // The compensation columns only exist for a compensation search.
    if (lastQuery?.compensation) {
      cols.push(
        {
          header: "Currency",
          accessorKey: "currency",
          size: 90,
          Cell: ({ cell }) => cell.getValue() || "-",
        },
        {
          header: "Compensation",
          accessorKey: "compensation",
          Cell: ({ cell }) =>
            cell.getValue() !== null && cell.getValue() !== undefined
              ? Number(cell.getValue()).toLocaleString()
              : "-",
        },
      );
    }
    cols.push(
      {
        header: "Country",
        accessorKey: "country",
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        header: "City",
        accessorKey: "city",
        Cell: ({ cell }) => cell.getValue() || "-",
      },
    );
    return cols;
  }, [lastQuery]);

  // ----------------------------------------------------------------- render
  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-body">
          <h5 className="mb-3">Advanced Search</h5>
          <form onSubmit={onSearch} onReset={onReset}>
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">Search Type</label>
                <Select
                  value={{
                    value: form.searchType,
                    label: isCandidateSearch ? "By Candidate" : "By Role",
                  }}
                  options={[
                    { value: "by_candidate", label: "By Candidate" },
                    { value: "by_role", label: "By Role" },
                  ]}
                  onChange={(o) => setField("searchType", o.value)}
                  isSearchable={false}
                />
              </div>
              <div className="col-md-4 d-flex align-items-end">
                <div className="form-check mb-2">
                  <input
                    id="adv-compensation"
                    type="checkbox"
                    className="form-check-input"
                    checked={form.compensation}
                    onChange={(e) => setField("compensation", e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="adv-compensation">
                    Compensation
                  </label>
                </div>
              </div>
            </div>

            {!isCandidateSearch && (
              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label">Roles</label>
                  <Select
                    isMulti
                    closeMenuOnSelect={false}
                    value={form.roles}
                    options={myRoles}
                    onChange={(v) => setField("roles", v || [])}
                    placeholder="Select roles"
                  />
                </div>
              </div>
            )}

            {isCandidateSearch && (
              <>
                <div className="row mb-3">
                  <div className="col-md-12">
                    <label className="form-label">Skills / keywords</label>
                    <AsyncCreatableSelect
                      isMulti
                      cacheOptions
                      value={form.keyword}
                      loadOptions={loadSkills}
                      onChange={(v) => setField("keyword", v || [])}
                      placeholder="Type a skill, press enter to add"
                      formatCreateLabel={(v) => `Add "${v}"`}
                    />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label className="form-label">Name</label>
                    <input
                      className="form-control"
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Designation</label>
                    <input
                      className="form-control"
                      value={form.designation}
                      onChange={(e) => setField("designation", e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">LinkedIn URL</label>
                    <input
                      className="form-control"
                      value={form.linkedin_url}
                      onChange={(e) => setField("linkedin_url", e.target.value)}
                    />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Current Company</label>
                    <AsyncSelect
                      isMulti
                      cacheOptions
                      value={form.company}
                      loadOptions={loadCompanies}
                      onChange={(v) => setField("company", v || [])}
                      placeholder="Type to search companies"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Previous Company</label>
                    <AsyncSelect
                      isMulti
                      cacheOptions
                      value={form.prev_company}
                      loadOptions={loadCompanies}
                      onChange={(v) => setField("prev_company", v || [])}
                      placeholder="Type to search companies"
                    />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Major Function</label>
                    <Select
                      isClearable
                      value={form.dutymaster}
                      options={functions}
                      onChange={(v) => setField("dutymaster", v)}
                      placeholder="-"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label d-flex justify-content-between">
                      <span>Region / Countries</span>
                      <span className="form-check">
                        <input
                          id="adv-region-only"
                          type="checkbox"
                          className="form-check-input"
                          checked={form.region_only}
                          onChange={(e) => setField("region_only", e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="adv-region-only">
                          Only Region?
                        </label>
                      </span>
                    </label>
                    {form.region_only ? (
                      <Select
                        isMulti
                        closeMenuOnSelect={false}
                        value={form.regions}
                        options={regionOptions}
                        onChange={(v) => setField("regions", v || [])}
                        placeholder="Select regions"
                      />
                    ) : (
                      <Select
                        isMulti
                        closeMenuOnSelect={false}
                        value={form.countries}
                        options={countryOptions}
                        onChange={(v) => setField("countries", v || [])}
                        placeholder="Select countries"
                      />
                    )}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Major Industry</label>
                    <Select
                      isClearable
                      value={form.industrymaster}
                      options={masterIndustries}
                      onChange={(v) => setField("industrymaster", v)}
                      placeholder="-"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Industry</label>
                    <Select
                      isMulti
                      closeMenuOnSelect={false}
                      value={form.industries}
                      options={minorIndustries}
                      onChange={(v) => setField("industries", v || [])}
                      placeholder={
                        form.industrymaster
                          ? "Select industries"
                          : "Select a major industry first"
                      }
                      isDisabled={!form.industrymaster}
                    />
                  </div>
                </div>
              </>
            )}

            <div className="d-flex gap-2">
              <button type="submit" className="btn btn-primary">
                Search
              </button>
              <button type="reset" className="btn btn-secondary">
                Reset
              </button>
              {searched && hasMore && (
                <button type="button" className="btn btn-info" onClick={onMore}>
                  More Data
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="card">
          <div className="card-body">
            <h6 className="mb-3">
              Add {selectedIds.length} selected candidate
              {selectedIds.length > 1 ? "s" : ""} to a role
            </h6>
            <div className="row">
              <div className="col-md-6">
                <Select
                  isClearable
                  value={targetRole}
                  options={myRoles}
                  onChange={setTargetRole}
                  placeholder="Select the role"
                />
              </div>
              <div className="col-md-3">
                <button type="button" className="btn btn-success" onClick={addToRole}>
                  Add to Role
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Candidates</h5>
            {rows.length > 0 && (
              <button
                type="button"
                className="btn btn-sm btn-success"
                onClick={exportExcel}
              >
                Export to Excel
              </button>
            )}
          </div>
          {rows.length > 0 ? (
            <MaterialReactTable
              columns={columns}
              data={rows}
              getRowId={(row) => String(row.id)}
              enableRowSelection
              enablePagination
              enableSorting
              enableColumnFilters
              onRowSelectionChange={setRowSelection}
              state={{ rowSelection }}
              initialState={{
                pagination: { pageIndex: 0, pageSize: 50 },
                showColumnFilters: true,
              }}
              muiTableBodyRowProps={({ row }) => ({
                sx: row.original.offer_company !== null
                  ? { backgroundColor: "rgba(255, 0, 0, 0.08)" }
                  : undefined,
              })}
            />
          ) : (
            <p className="text-center mb-0">
              <b>
                {searched
                  ? "No candidate found."
                  : "Choose your filters and press Search."}
              </b>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdvSearchCandidates;
